import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { objectSerializer } from "helpers/utils";
import { calPaginatorRecord } from "helpers/paginator";
import { toast } from "react-toastify";

const projectUrl = `/crm/reports/change_order`;

export const getData = createAsyncThunk(
  "ReportChangeOrder/getData",
  async (params) => {
    try {
      const response = await apiAxios.get(projectUrl, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const exportData = createAsyncThunk(
  "ReportChangeOrder/exportData",
  async (params) => {
    const { page, per_page, ...rest } = params
    console.log(rest, page, per_page)

    const response = await apiAxios.get(`${projectUrl}/export`, {params, responseType: 'blob'})
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'project-schedule.xlsx');
    document.body.appendChild(link);
    link.click();
  }
)

export const getColumns = createAsyncThunk(
  "ReportChangeOrder/getColumns",
  async () => {
    try {
      const response = await apiAxios.get(`${projectUrl}/attributes`)
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getChangeOrderSummary = createAsyncThunk(
  "ReportChangeOrder/getChangeOrderSummary",
  async (params) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/summary`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getChangeOrderStatusThisMonth = createAsyncThunk(
  "ReportChangeOrder/getChangeOrderStatusThisMonth",
  async () => {
    try {
      const response = await apiAxios.get(`${projectUrl}/status_this_month`)
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

const isRejectedAction = (action) => (action.type.startsWith("ReportChangeOrder/")) && action.type.endsWith("/rejected");

const changeOrderSlice = createSlice({
  name: "report_change_order",
  initialState: {
    data: [],
    totalRows: 0,
    paginate: {
      totalPagesCount: 0,
      currentPage: 1,
      pageSize: 100,
      fromRecord: 0,
      toRecord: 1,
    },
    currentParams: {page: 1, per_page: 100},
    currentParamsOriginal: {page: 1, per_page: 100},
    sortParams: {attribute: 'id', direction: 'desc'},
    loading: false,
    exporting: false,
    sortColumns: {},
    filterTags: [],
    summaryData: [],
    statusThisMonthData: []
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.paginate.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.paginate.pageSize = parseInt(action.payload)
    },
    setCurrentParams: (state, action) => {
      state.currentParams = action.payload
    },
    resetCurrentParams: (state) => {
      state.currentParams = {page: 1, per_page: 100}
    },
    setSortParams: (state, action) => {
      state.sortParams = action.payload
    },
    setFilterTag: (state, action) => {
      state.filterTags = action.payload
    },
    resetFilterTag: (state) => {
      state.filterTags = []
    },
    setCurrentParamsOriginal: (state, action) => {
      state.currentParamsOriginal = action.payload
    },
    resetCurrentParamsOriginal: (state) => {
      state.currentParamsOriginal = {page: 1, per_page: 100}
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getData.fulfilled, (state, action) => {
        state.loading = false
        const results = action.payload

        state.data = objectSerializer(results.rows)
        state.totalRows = results.total_rows

        const paginate           = state.paginate
        const calRecords         = calPaginatorRecord(paginate.currentPage, paginate.pageSize, state.totalRows)
        paginate.totalPagesCount = calRecords.totalPagesCount
        paginate.fromRecord      = calRecords.beginRecords
        paginate.toRecord        = calRecords.lastRecords
      })
      .addCase(getColumns.fulfilled, (state, action) => {
        state.sortColumns = action.payload.sort_columns
      })
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Data has been exported successfully.")
      })
      .addCase(getChangeOrderSummary.fulfilled, (state, action) => {
        state.summaryData = action.payload
      })
      .addCase(getChangeOrderStatusThisMonth.fulfilled, (state, action) => {
        state.statusThisMonthData = action.payload
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
});

export const {
  setCurrentPage,
  setPageSize,
  setCurrentParams,
  resetCurrentParams,
  setSortParams,
  setFilterTag,
  resetFilterTag,
  setCurrentParamsOriginal,
  resetCurrentParamsOriginal
} = changeOrderSlice.actions;

export default changeOrderSlice.reducer;